import { Link, NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, Plus, LogOut, LayoutDashboard, User, Compass, Info, Shield } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import ModeSwitcher, { useBrand, isTontineRoute } from "./ModeSwitcher";
import { useLocation } from "react-router-dom";

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const tontineMode = isTontineRoute(location.pathname);

  const links = tontineMode
    ? [
        { to: "/tontines", label: "Mes tontines", icon: LayoutDashboard },
        { to: "/about", label: "À propos", icon: Info },
      ]
    : [
        { to: "/explore", label: "Explorer", icon: Compass },
        { to: "/about", label: "À propos", icon: Info },
      ];

  const createPath = tontineMode ? "/tontines/new" : "/cagnotte/new";
  const createLabel = tontineMode ? "Créer tontine" : "Créer";

  const navLinkClass = ({ isActive }) =>
    `px-4 py-2 rounded-full text-sm font-medium transition-all ${
      isActive
        ? "bg-secondary text-foreground"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`;

  return (
    <header className="sticky top-0 z-50 glass border-b" data-testid="navbar">
      {/* Row 1 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to={tontineMode ? "/tontines" : "/"} className="flex items-center gap-2 group min-w-0" data-testid="navbar-logo">
          <img
            src={brand.logo}
            alt={brand.name}
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl object-cover shadow-md group-hover:scale-105 transition-transform shrink-0"
          />
          <span className="font-display font-bold tracking-tight text-base sm:text-lg truncate">
            <span className="sm:hidden">{brand.short}</span>
            <span className="hidden sm:inline">{brand.name}</span>
          </span>
        </Link>

        {/* Mode switcher (desktop center) */}
        <div className="hidden md:flex">
          <ModeSwitcher />
        </div>

        {/* Desktop nav links */}
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={navLinkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-full h-9 w-9" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {user ? (
            <>
              <Button onClick={() => navigate(createPath)} className="hidden sm:inline-flex btn-gradient rounded-full font-medium" data-testid="nav-create">
                <Plus className="h-4 w-4 mr-1" /> {createLabel}
              </Button>
              <Button onClick={() => navigate(createPath)} size="icon" className="sm:hidden btn-gradient rounded-full h-9 w-9" data-testid="nav-create-mobile" aria-label="Créer"><Plus className="h-4 w-4" /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full" data-testid="user-menu-trigger">
                    <Avatar className="h-9 w-9 ring-2 ring-transparent hover:ring-amber-400/50 transition-all">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="gradient-gold-green text-white font-semibold">{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold truncate">{user.name}</span>
                      <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> Cagnottes</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/tontines")} data-testid="menu-tontines"><LayoutDashboard className="h-4 w-4 mr-2" /> Tontines</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" /> Profil</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" /> Administration</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button onClick={() => navigate("/register")} className="btn-gradient rounded-full text-sm px-4 sm:px-6" data-testid="nav-register">Commencer</Button>
          )}
        </div>
      </div>

      {/* Row 2 mobile: ModeSwitcher + links */}
      <div className="md:hidden border-t border-border/60">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 flex items-center gap-2 overflow-x-auto" data-testid="mobile-nav-row">
          <ModeSwitcher className="shrink-0" />
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <l.icon className="h-3.5 w-3.5" />
              {l.label}
            </NavLink>
          ))}
          {!user && (
            <NavLink to="/login" className="ml-auto shrink-0 px-3 py-1.5 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="mobile-nav-login">Connexion</NavLink>
          )}
        </div>
      </div>

      {/* Row 3 desktop nav links if md but not lg */}
      <div className="hidden md:flex lg:hidden border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-1 w-full">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={navLinkClass}>
              <l.icon className="h-3.5 w-3.5 inline mr-1.5" />
              {l.label}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
}
